import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from './ui/ModalCloseButton';
import { getOfferDescription, getOfferFeatures } from '../utils/billing';
import { getAuthHeader } from '../utils/auth';
import { BACKEND_URL } from '../utils/backendUrl';
import { trackEvent } from '../utils/telemetry';

const BillingOptionsModal = ({
  isOpen,
  portalMounted,
  billingOverlayOnly = false,
  title,
  description,
  showSubscriptionTabs,
  billingModalTab,
  onBillingTabChange,
  activeBillingOffers,
  selectedBillingPackageId,
  onSelectOffer,
  getOfferTitle,
  formatOfferPrice,
  getOfferBadge,
  getOfferSavings,
  getOfferFinePrint,
  isRecommendedOffer,
  creditPackOffers,
  selectedOffer,
  billingLoadingPackageId,
  billingError,
  isLaunching,
  onClose,
  onContinue,
  effectiveSubscriptionActive = false,
  onManageSubscription = null,
  onRefreshUser = null
}) => {
  const [promoCode, setPromoCode] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const paramCode = urlParams.get('promo') || urlParams.get('ref');
      if (paramCode) return paramCode.toUpperCase();
      return sessionStorage.getItem('cmp_pending_promo') || '';
    } catch (_) {
      return '';
    }
  });
  const [promoStatus, setPromoStatus] = useState(null); // 'success' | 'error' | null
  const [promoMessage, setPromoMessage] = useState('');
  const [isSubmittingPromo, setIsSubmittingPromo] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return Boolean(urlParams.get('promo') || urlParams.get('ref') || sessionStorage.getItem('cmp_pending_promo'));
    } catch (_) {
      return false;
    }
  });

  const handleRedeemPromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim() || isSubmittingPromo) return;
    setIsSubmittingPromo(true);
    setPromoMessage('');
    setPromoStatus(null);
    try {
      const authHeader = getAuthHeader();
      const res = await fetch(`${BACKEND_URL}/api/billing/redeem-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ code: promoCode.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPromoStatus('success');
        setPromoMessage(data.message || 'Promo code applied! Free credit added.');
        trackEvent('promo_code_redeemed', { code: data.code });
        if (typeof onRefreshUser === 'function') {
          onRefreshUser();
        }
      } else {
        setPromoStatus('error');
        setPromoMessage(data.detail || 'Could not apply promo code.');
      }
    } catch (err) {
      setPromoStatus('error');
      setPromoMessage('Network error. Please try again.');
    } finally {
      setIsSubmittingPromo(false);
    }
  };

  if (!isOpen || !portalMounted || typeof document === 'undefined') return null;

  // Always render at the same column count on a given breakpoint so the modal
  // doesn't change size when the user switches between credit packs (3 cards)
  // and subscriptions (2 cards). On small screens BOTH tabs render as a
  // single column so the inner scroller takes the size shift (not the modal
  // panel itself).
  const gridClassName = billingModalTab === 'credits'
    ? 'grid grid-cols-1 gap-2.5 md:grid-cols-3'
    : 'mx-auto grid w-full max-w-3xl grid-cols-1 gap-2.5 md:grid-cols-2';

  const modal = (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-gray-950/50 p-3 backdrop-blur-md ${billingOverlayOnly ? 'z-[100000]' : 'z-[99999]'}`}
      onClick={() => {
        if (!isLaunching) onClose?.();
      }}
    >
      <div
        // Fixed height (not min-h) locks the modal panel at a stable size so
        // switching tabs never changes the dialog footprint. Using dvh so the
        // value tracks the dynamic viewport on iOS Safari and Android Chrome.
        className="relative flex h-[min(92dvh,640px)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white text-gray-950 shadow-[0_32px_90px_rgba(15,23,42,0.22)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-options-title"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-shrink-0 border-b border-gray-100 px-4 py-3.5 sm:px-5 sm:py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 pr-2">
                <h3 id="billing-options-title" className="text-lg font-extrabold tracking-tight text-gray-950 sm:text-2xl">
                  {title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">{description}</p>
              </div>
              <ModalCloseButton
                onClick={onClose}
                className={isLaunching ? 'pointer-events-none opacity-40' : ''}
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-4 pb-3 sm:px-5 sm:pb-4">
            {showSubscriptionTabs && (
              <div className="mb-3 flex justify-center pt-1">
                <div
                  className="inline-flex w-full max-w-md rounded-full border border-gray-200 bg-gray-100 p-0.5"
                  role="tablist"
                  aria-label="Billing type"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={billingModalTab === 'credits'}
                    onClick={() => onBillingTabChange?.('credits')}
                    className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition ${
                      billingModalTab === 'credits'
                        ? 'bg-zinc-900 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-950'
                    }`}
                  >
                    Credit packs
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={billingModalTab === 'subscriptions'}
                    onClick={() => onBillingTabChange?.('subscriptions')}
                    className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition ${
                      billingModalTab === 'subscriptions'
                        ? 'bg-zinc-900 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-950'
                    }`}
                  >
                    Subscriptions
                  </button>
                </div>
              </div>
            )}

            <p className="mb-3 text-left text-xs leading-relaxed text-gray-600 sm:text-sm">
              {billingModalTab === 'credits' ? (
                <span className="font-medium text-gray-700">
                  1 credit creates a brand new AI planning result. <strong>All theme customizations, moodboard adjustments, shopping exports, and invites are 100% free and unlimited.</strong>
                </span>
              ) : (
                effectiveSubscriptionActive ? (
                  <span className="font-medium text-gray-700">Your subscription is active. You can add extra credit packs anytime.</span>
                ) : (
                  <span className="font-medium text-gray-700">Subscriptions include recurring planning credits based on your selected plan. Unused credits rollover forever.</span>
                )
              )}
            </p>

            <div className="flex-1 overflow-y-auto overscroll-contain px-0.5">
              {billingModalTab === 'subscriptions' && effectiveSubscriptionActive ? (
                <div className="mx-auto max-w-md w-full rounded-2xl border border-gray-200 bg-stone-50 p-6 text-center my-8 shadow-sm">
                  <h4 className="text-lg font-bold text-gray-950">Your subscription is active</h4>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    Unused credits stay in your account. If you run out of credits, you can add extra credit packs anytime.
                  </p>
                  {onManageSubscription && (
                    <button
                      type="button"
                      onClick={onManageSubscription}
                      className="mt-5 inline-flex h-10 items-center justify-center rounded-xl border border-zinc-200 bg-white px-5 text-sm font-bold text-zinc-900 transition hover:bg-stone-50 shadow-sm"
                    >
                      Manage or cancel subscription
                    </button>
                  )}
                </div>
              ) : (
                <div className={`${gridClassName} pt-2`} role="radiogroup" aria-label="Billing options">
                  {activeBillingOffers.map((pkg) => {
                    const isSelected = selectedBillingPackageId === pkg.id;
                    const isRecommended = isRecommendedOffer?.(pkg);
                    const savings = getOfferSavings?.(pkg);
                    const recommendationLabel = pkg.id === 'credit_5' ? 'Popular' : 'Best value';
                    const displayBadge = pkg.kind === 'subscription'
                      ? (pkg.interval === 'year'
                          ? 'Includes 24 credits per year · delivered as 2 credits each month'
                          : 'Includes 2 credits/month · rollover included')
                      : `Includes ${pkg.credits} planning result${pkg.credits > 1 ? 's' : ''}`;
                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={billingLoadingPackageId !== null}
                      onClick={() => onSelectOffer?.(pkg.id)}
                      className={`relative flex min-h-[136px] flex-col rounded-2xl border-2 p-3.5 pt-5 text-left transition disabled:opacity-60 sm:min-h-[150px] sm:p-4 sm:pt-5 ${
                        isSelected
                          ? 'border-zinc-900 bg-stone-50 shadow-md ring-2 ring-zinc-200'
                          : 'border-gray-200 bg-white hover:border-stone-300 hover:shadow-sm'
                      }`}
                    >
                      {isRecommended && (
                        <span className="absolute -top-2 left-4 rounded-full bg-zinc-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                          {recommendationLabel}
                        </span>
                      )}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h4 className="text-base font-extrabold text-gray-950 sm:text-lg">
                            {getOfferTitle?.(pkg)}
                          </h4>
                          <p className="mt-0.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">{displayBadge}</p>
                        </div>
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                            isSelected ? 'border-zinc-900 bg-zinc-900' : 'border-gray-300 bg-white'
                          }`}
                          aria-hidden="true"
                        >
                          {isSelected && (
                            <span className="h-2 w-2 rounded-full bg-white" />
                          )}
                        </span>
                      </div>
                      <p className="mt-3 text-3xl font-extrabold tracking-tight text-gray-950 sm:text-4xl">
                        {billingLoadingPackageId === pkg.id ? 'Opening…' : formatOfferPrice?.(pkg)}
                      </p>
                      {pkg.kind === 'subscription' && (
                        <p className="mt-1 text-sm text-gray-500">
                          {pkg.interval === 'year' ? 'Billed yearly' : 'Billed monthly'}
                        </p>
                      )}
                      {savings ? (
                        <span className="mt-2 inline-flex w-fit rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                          {savings}
                        </span>
                      ) : (
                        <span className="mt-2 block h-5" aria-hidden="true" />
                      )}
                      <div className="mt-auto pt-3 flex flex-col gap-2">
                        {getOfferFinePrint?.(pkg) && (
                          <span className="text-[10px] font-bold text-stone-500 uppercase tracking-wider block">
                            {getOfferFinePrint(pkg)}
                          </span>
                        )}
                        <ul className="space-y-1 text-[11px] text-gray-600">
                          {getOfferFeatures(pkg).map((feature, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <svg className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="leading-snug">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

            {billingError && (
              <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {billingError}
              </p>
            )}

            {/* Promotional / Referral Code Box */}
            <div className="mt-3 rounded-2xl border border-stone-200/80 bg-stone-50/70 p-3 text-xs">
              {!showPromoInput && promoStatus !== 'success' ? (
                <button
                  type="button"
                  onClick={() => setShowPromoInput(true)}
                  className="text-xs font-bold text-stone-600 hover:text-stone-900 underline transition"
                >
                  Have a promotional or referral code?
                </button>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-bold text-stone-700">Redeem Promo Code</span>
                    {promoStatus !== 'success' && (
                      <button
                        type="button"
                        onClick={() => setShowPromoInput(false)}
                        className="text-[11px] text-stone-400 hover:text-stone-600"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  {promoStatus === 'success' ? (
                    <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 p-2.5 text-emerald-800 font-semibold">
                      <span>✓</span>
                      <span>{promoMessage}</span>
                    </div>
                  ) : (
                    <form onSubmit={handleRedeemPromo} className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="e.g. CELEBRATE1"
                        className="flex-1 rounded-xl border border-stone-300 bg-white px-3 py-1.5 text-xs font-bold tracking-wider uppercase text-stone-900 focus:border-stone-800 focus:outline-none"
                        disabled={isSubmittingPromo}
                      />
                      <button
                        type="submit"
                        disabled={isSubmittingPromo || !promoCode.trim()}
                        className="rounded-xl bg-stone-900 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-stone-800 disabled:opacity-40"
                      >
                        {isSubmittingPromo ? 'Applying...' : 'Apply'}
                      </button>
                    </form>
                  )}
                  {promoStatus === 'error' && (
                    <p className="mt-1.5 text-[11px] font-medium text-red-600">
                      {promoMessage}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-3 flex-shrink-0 border-t border-gray-100 pt-3">
              <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLaunching}
                  className="h-11 rounded-2xl bg-gray-100 px-4 text-sm font-bold text-gray-800 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 sm:min-w-[120px]"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={onContinue}
                  disabled={isLaunching || !selectedOffer}
                  className="h-11 min-w-0 rounded-2xl bg-zinc-900 px-3 text-sm font-bold text-white shadow-md transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-gray-300 sm:max-w-md sm:flex-none sm:px-6"
                >
                  {selectedOffer ? (
                    <>
                      <span className="sm:hidden">Continue · {formatOfferPrice?.(selectedOffer)}</span>
                      <span className="hidden sm:inline">Continue to checkout · {formatOfferPrice?.(selectedOffer)}</span>
                    </>
                  ) : (
                    'Select an option'
                  )}
                </button>
              </div>
              <p className="mt-2 hidden text-[11px] leading-relaxed text-gray-400 sm:block">
                {creditPackOffers?.some((pkg) => pkg.price_source === 'stripe_price')
                  ? 'Local totals for credit packs are confirmed in Stripe Checkout. Subscriptions use configured plan prices.'
                  : 'Prices follow your configured country and currency rules.'}
              </p>
            </div>
          </div>
        </div>

        {isLaunching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/88 backdrop-blur-sm">
            <div className="rounded-2xl border border-gray-200 bg-white px-6 py-5 text-center shadow-2xl">
              <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-zinc-900" />
              <p className="text-base font-semibold text-gray-950">Opening secure checkout…</p>
              <p className="mt-1 text-sm text-gray-500">Stripe is preparing your session.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default BillingOptionsModal;
